import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import {
  freeTextBoxes,
  hasTitleText,
  isPlaceholder,
  readPresentation,
} from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionPlatzhalterAbschluss: Lesson = {
  id: "abschlussuebung",
  title: "Abschlussübung: eine saubere Folienfolge",
  summary: "Alles zusammen: Layout, Titel, Inhalt im Platzhalter, keine losen Kästen.",
  durationMinutes: 10,
  learningGoals: [
    "Eine mehrseitige Folienfolge vollständig über Layouts aufbauen",
    "Das Ergebnis in der Gliederungsansicht gegenprüfen",
  ],
  steps: [
    {
      id: "der-test",
      title: "Der Zehn-Sekunden-Test",
      explanation: [
        "Bevor eine Präsentation aus der Hand geht, lohnt sich ein kurzer Test, der drei " +
          "der häufigsten Strukturfehler auf einmal findet.",
        "Öffne die Gliederungsansicht. Steht dort für jede Folie eine Titelzeile? Steht " +
          "unter den Inhaltsfolien der Text, den du auf der Folie siehst? Wenn beides " +
          "stimmt, sind die Folien über Platzhalter aufgebaut und der Rest der " +
          "Präsentation – Design wechseln, exportieren, vorlesen lassen – funktioniert " +
          "zuverlässig.",
        "Fehlt eine Titelzeile, fehlt der Titel. Ist die Folie in der Gliederung leer, " +
          "obwohl sie voller Text ist, steckt der Text in freien Textfeldern. Beides ist " +
          "in zwei Minuten repariert, solange die Präsentation noch klein ist.",
      ],
      keyPoints: [
        "Ansicht → Gliederungsansicht als Abschlusskontrolle.",
        "Keine Titelzeile = kein Titel. Leere Folie in der Gliederung = freie Textfelder.",
        "Start → Zurücksetzen bringt verschobene Platzhalter zurück.",
      ],
    },
    {
      id: "abschluss",
      title: "Abschlussübung",
      explanation: [
        "Bring alles zusammen: eine Präsentation aus mindestens vier Folien, jede mit " +
          "Titel, der Inhalt vollständig in Platzhaltern, kein loses Textfeld.",
      ],
      keyPoints: [
        "Strg + M für eine neue Folie, Start → Layout für das passende Layout.",
        "Text immer in den vorhandenen Platzhalter tippen.",
        "Zum Schluss die Gliederungsansicht gegenlesen.",
      ],
      task: {
        instruction:
          "Deine Präsentation soll mindestens vier Folien haben, jede mit gefülltem " +
          "Titelplatzhalter, mindestens zwei Folien mit gefülltem Inhaltsplatzhalter und " +
          "kein einziges frei gezogenes Textfeld.",
        hint: "In der Gliederungsansicht siehst du alle drei Punkte auf einen Blick.",
        check: async () => {
          const snapshot = await readPresentation();

          if (snapshot.slides.length === 0) {
            return fail(
              "In der Präsentation ist noch keine Folie vorhanden.",
              "Lege zuerst Folien an (Strg + M).",
            );
          }
          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Die Art einer Form (Platzhalter oder Textfeld)",
              "1.4",
              "Prüfe die drei Punkte selbst in der Gliederungsansicht und im Auswahlbereich.",
            );
          }

          const anzahl = snapshot.slides.length;
          const ohneTitel = snapshot.slides.filter((folie) => !hasTitleText(folie));
          const mitInhalt = snapshot.slides.filter((folie) =>
            folie.shapes.some(
              (form) =>
                isPlaceholder(form) &&
                form.paragraphs.length > 0 &&
                form.placeholder?.type !== "Title" &&
                form.placeholder?.type !== "CenterTitle" &&
                !/^(title|titel)\b/i.test(form.name ?? ""),
            ),
          );
          const textfelder = snapshot.slides.flatMap(freeTextBoxes);

          return fromCriteria(
            [
              criterion(`Mindestens vier Folien (gefunden: ${anzahl})`, anzahl >= 4),
              criterion(
                ohneTitel.length === 0
                  ? "Jede Folie hat einen gefüllten Titel"
                  : `Ohne Titel: Folie ${ohneTitel.map((folie) => folie.index + 1).join(", ")}`,
                ohneTitel.length === 0,
              ),
              criterion(
                `Mindestens zwei Folien mit gefülltem Inhaltsplatzhalter (gefunden: ${mitInhalt.length})`,
                mitInhalt.length >= 2,
              ),
              criterion(
                textfelder.length === 0
                  ? "Kein frei gezogenes Textfeld"
                  : `Noch ${textfelder.length} freie Textfelder auf Folie ${[
                      ...new Set(textfelder.map((form) => form.slideIndex + 1)),
                    ].join(", ")}`,
                textfelder.length === 0,
              ),
            ],
            {
              success:
                "Die Folienfolge ist vollständig über Layouts und Platzhalter aufgebaut – " +
                "damit übersteht sie jeden Designwechsel.",
              failure: "Fast geschafft. Diese Punkte fehlen noch:",
              hint: "Fehlt auf einer Folie der passende Platzhalter, weise ihr zuerst über „Start“ → „Layout“ ein anderes Layout zu.",
            },
          );
        },
      },
    },
  ],
};
